import type { TEDNotice } from "./tedClient";

const MS_PER_DAY = 86_400_000;
const ROLLING_12M_MS = 365 * MS_PER_DAY;

export function detectSingleBidder(
  notice: TEDNotice,
): { flag: boolean; reason: string; undetermined?: boolean } {
  const c = notice.bidCount ?? notice.tenderApplicationCount;
  if (c == null) {
    return {
      flag: false,
      undetermined: true,
      reason: "Tenderer count is not available for this notice (undetermined).",
    };
  }
  if (c === 1) {
    return { flag: true, reason: "Only one valid tender (single-bid) pattern detected in structured submission data." };
  }
  return { flag: false, reason: `More than one tenderer reported (${c}).` };
}

function norm(s: string): string {
  return s.trim().toLowerCase();
}

function parseIsoToMs(d: string): number | null {
  if (!d) return null;
  const t = Date.parse(d.length <= 10 ? `${d}T00:00:00Z` : d);
  return Number.isFinite(t) ? t : null;
}

/**
 * True when the same contracting authority and same winner recurs 3+ times
 * in the 12 months ending on the current notice’s publication date.
 */
export function detectRepeatWinner(
  notice: TEDNotice,
  history: TEDNotice[],
): { flag: boolean; repeatCount: number; reason: string } {
  if (!norm(notice.buyerName) || !notice.awardedTo) {
    return { flag: false, repeatCount: 0, reason: "Missing buyer or winner for pattern comparison." };
  }
  const t0 = parseIsoToMs(notice.publicationDate);
  if (t0 == null) {
    return { flag: false, repeatCount: 0, reason: "Publication date is not parseable for the rolling window." };
  }
  const windowStart = t0 - ROLLING_12M_MS;
  const b = norm(notice.buyerName);
  const w = norm(notice.awardedTo);
  const inWindow: TEDNotice[] = [notice, ...history].filter((h) => {
    const t = parseIsoToMs(h.publicationDate);
    if (t == null || t < windowStart || t > t0) return false;
    if (!h.awardedTo) return false;
    return norm(h.buyerName) === b && norm(h.awardedTo) === w;
  });
  const repeatCount = inWindow.length;
  if (repeatCount >= 3) {
    return {
      flag: true,
      repeatCount,
      reason: `The same authority awarded the same economic operator at least three times in a rolling 12-month window (matches: ${repeatCount}).`,
    };
  }
  return {
    flag: false,
    repeatCount,
    reason: "Repeat buyer–winner pattern is below the 3+ threshold in the 12-month window.",
  };
}

/**
 * True when the signed contract (award) value deviates from the published estimate
 * by more than 20% in either direction.
 */
export function detectAbnormalValueRatio(
  notice: TEDNotice,
): { flag: boolean; ratio: number; reason: string } {
  const est = notice.estimatedValue;
  const awd = notice.awardedValue;
  if (est == null || awd == null || est <= 0) {
    return { flag: false, ratio: 0, reason: "Estimate and award are both required to compute a ratio." };
  }
  const ratio = awd / est;
  const delta = (awd - est) / est;
  if (Math.abs(delta) > 0.2) {
    return {
      flag: true,
      ratio,
      reason: `Award deviates from estimate by more than 20% (delta ${(delta * 100).toFixed(1)}%).`,
    };
  }
  return {
    flag: false,
    ratio,
    reason: `Value ratio within the ±20% band around the estimate (delta ${(delta * 100).toFixed(1)}%).`,
  };
}

export const RED_FLAG_CODES = {
  SINGLE_BIDDER: "SINGLE_BIDDER",
  REPEAT_WINNER: "REPEAT_WINNER",
  ABNORMAL_VALUE: "ABNORMAL_VALUE",
} as const;

export function scoreTransparency(
  notice: TEDNotice,
  history: TEDNotice[],
): { score: number; flags: string[]; details: object } {
  const single = detectSingleBidder(notice);
  const repeat = detectRepeatWinner(notice, history);
  const abnormal = detectAbnormalValueRatio(notice);

  let score = 100;
  const flags: string[] = [];
  if (single.flag) {
    flags.push(RED_FLAG_CODES.SINGLE_BIDDER);
    score -= 30;
  }
  if (repeat.flag) {
    flags.push(RED_FLAG_CODES.REPEAT_WINNER);
    score -= 25;
  }
  if (abnormal.flag) {
    flags.push(RED_FLAG_CODES.ABNORMAL_VALUE);
    score -= 20;
  }
  score = Math.max(0, Math.min(100, Math.round(score)));

  return {
    score,
    flags,
    details: { single, repeat, abnormal },
  };
}

export type RedFlagResult = ReturnType<typeof scoreTransparency>;
